import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Wallet as WalletIcon, ArrowDownToLine, Smartphone, CreditCard, Building2, Banknote } from "lucide-react";

const METHODS = [
    { v: "mpesa", l: "M-Pesa", icon: Smartphone },
    { v: "card", l: "Card", icon: CreditCard },
    { v: "bank", l: "Bank transfer", icon: Building2 },
    { v: "cash", l: "Cash deposit", icon: Banknote },
];

const QUICK = [500, 1000, 2500, 5000, 10000];

function fmt(n) {
    return new Intl.NumberFormat("en-KE", { maximumFractionDigits: 2 }).format(n || 0);
}

export default function WalletTab() {
    const [wallet, setWallet] = useState(null);
    const [amount, setAmount] = useState(1000);
    const [method, setMethod] = useState("mpesa");
    const [loading, setLoading] = useState(false);
    const [recent, setRecent] = useState([]);

    const load = async () => {
        const [w, t] = await Promise.all([
            api.get("/wallet"),
            api.get("/transactions?kind=TOPUP"),
        ]);
        setWallet(w.data);
        setRecent(t.data);
    };

    useEffect(() => { load(); }, []);

    const topup = async (e) => {
        e?.preventDefault();
        if (!amount || amount <= 0) return;
        setLoading(true);
        try {
            const { data } = await api.post("/wallet/topup", { amount: Number(amount), method });
            toast.success(`Top-up successful. New balance KES ${fmt(data.balance)}`);
            await load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-8" data-testid="wallet-page">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <div className="lg:col-span-1 rounded-3xl p-1 og-gradient">
                    <div className="rounded-[1.4rem] bg-onego-navy p-7 text-white h-full" data-testid="wallet-balance-card">
                        <div className="flex items-center gap-2">
                            <WalletIcon size={18} />
                            <p className="text-xs uppercase tracking-widest font-semibold text-white/70">Available balance</p>
                        </div>
                        <p className="mt-4 font-display text-5xl font-black">KES {fmt(wallet?.balance)}</p>
                        <p className="mt-2 text-sm text-white/60">Currency: {wallet?.currency || "KES"}</p>
                    </div>
                </div>

                <form onSubmit={topup} className="lg:col-span-2 og-card p-7" data-testid="topup-form">
                    <h3 className="font-display text-2xl font-bold text-onego-navy">Top up wallet</h3>
                    <p className="text-sm text-slate-600">Choose an amount and a payment method. Funds appear instantly in demo mode.</p>

                    <div className="mt-5 flex flex-wrap gap-2">
                        {QUICK.map((q) => (
                            <button
                                type="button"
                                key={q}
                                onClick={() => setAmount(q)}
                                className={`px-4 py-2 rounded-full text-sm font-bold border transition ${amount === q ? "bg-onego-navy text-white border-onego-navy" : "border-slate-200 text-slate-700 hover:border-onego-navy"}`}
                                data-testid={`topup-quick-${q}`}
                            >
                                KES {q.toLocaleString()}
                            </button>
                        ))}
                    </div>

                    <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-semibold text-onego-navy">Amount (KES)</label>
                            <input
                                type="number"
                                min={1}
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                className="og-input mt-1"
                                data-testid="topup-amount-input"
                            />
                        </div>
                        <div>
                            <label className="text-sm font-semibold text-onego-navy">Method</label>
                            <select className="og-input mt-1" value={method} onChange={(e) => setMethod(e.target.value)} data-testid="topup-method-select">
                                {METHODS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
                            </select>
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="og-btn-gradient mt-6 inline-flex items-center gap-2 disabled:opacity-60"
                        data-testid="topup-submit-btn"
                    >
                        <ArrowDownToLine size={18} /> {loading ? "Processing\u2026" : `Top up KES ${fmt(amount)}`}
                    </button>
                </form>
            </div>

            <div className="og-card p-7">
                <h3 className="font-display text-2xl font-bold text-onego-navy">Recent top-ups</h3>
                <div className="mt-4 divide-y divide-slate-100">
                    {recent.length === 0 && <p className="py-6 text-sm text-slate-500">No top-ups yet.</p>}
                    {recent.map((t) => (
                        <div key={t.id} className="py-3 flex items-center justify-between" data-testid={`topup-row-${t.id}`}>
                            <div>
                                <p className="font-semibold text-onego-navy">{t.meta?.description || "Top-up"}</p>
                                <p className="text-xs text-slate-500">{new Date(t.created_at).toLocaleString()} &middot; {t.ref}</p>
                            </div>
                            <p className="font-display font-bold text-emerald-600">+ KES {fmt(t.amount)}</p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
